import { DiContainer } from '@jack-henry/frontend-utils/di';
import {
    defaultDialogOptions,
    mapExitReason,
} from '@treasury/domain-components/identity-verification-dialog/identity-verification-dialog.helpers';
import { SecurityMessage } from '@treasury/domain/channel/mappings/security';
import { IdentityDialogOptions, IdentityDialogResult, TmApiError } from '@treasury/domain/shared';
import { html } from 'lit';
import { OmegaDialogService } from '../../services/omega-dialog';
import { type OmegaIdentityVerificationDialog } from './omega-identity-verification-dialog';

import './omega-identity-verification-dialog';

/**
 * Opens the identity verification dialog with the given security message and options using Omega dialog.
 */
export async function openIdentityDialogLegacy<T = object>(
    securityMessage: SecurityMessage,
    options: Partial<IdentityDialogOptions> = {}
): Promise<IdentityDialogResult<T>> {
    const { forLogin, withoutUserInteraction, otpRequestFn, disableClose } = {
        ...defaultDialogOptions,
        ...options,
    };
    const diContainer = await DiContainer.getInstance();
    const dialogService = diContainer.get(OmegaDialogService);
    const handle = dialogService.open<OmegaIdentityVerificationDialog, IdentityDialogResult<T>>(
        html`<omega-identity-verification-dialog
            .securityMessage=${securityMessage}
            .disableClose=${disableClose}
            .forLogin=${forLogin}
            .withoutUserInteraction=${withoutUserInteraction}
            .otpRequestFn=${otpRequestFn}
        ></omega-identity-verification-dialog>`,
        'Identity Verification',
        {
            // component renders its own buttons
            renderButtons: false,
            width: 400,
            preventClose: true,
        }
    );

    const { reason, data } = await handle.closed;

    if (!data) {
        throw new Error('Identity dialog exited with no data.');
    }

    if (data instanceof TmApiError) {
        throw data;
    }

    const { response, message } = data;

    return {
        reason: mapExitReason(reason),
        response,
        message,
    };
}
